import { ShiftOptionDto } from '../../../api/scheduler/models/shift-option-dto';
import { UNASSIGNED_SHIFT } from '../attendance-log.constants';
import { AttendanceShiftOption } from '../models';

const SHIFT_ID = /^\d+$/;

export function toAttendanceShiftOption(dto: ShiftOptionDto): AttendanceShiftOption {
  return {
    id: dto.id,
    name: dto.name,
    baseCode: dto.baseCode,
    baseName: dto.baseName,
    custom: dto.custom,
  };
}

/** True for a shift ID or the unassigned marker; anything else in a hand-edited URL is dropped. */
export function isShiftFilterValue(value: string): boolean {
  return value === UNASSIGNED_SHIFT || SHIFT_ID.test(value);
}

/** Splits the filter's shift values into the `shift` IDs and `unassignedShift` flag the API takes. */
export function toShiftParams(shifts: string[]): { shift?: number[]; unassignedShift?: boolean } {
  const ids = shifts.filter((value) => value !== UNASSIGNED_SHIFT).map(Number);
  const unassigned = shifts.includes(UNASSIGNED_SHIFT);
  return {
    shift: ids.length ? ids : undefined,
    unassignedShift: unassigned || undefined,
  };
}

/** One chip of the shift filter: every Base's shift with the same name, selected together. */
export interface AttendanceShiftGroup {
  /** Stable key for the chip; the name without letter case or surrounding spaces. */
  key: string;
  /** The name as the first shift of the group spells it. */
  label: string;
  /** The shift IDs, as the filter holds them. */
  ids: string[];
  /** True only when every shift of the group is Base-specific; a default Day or Night shift makes it false. */
  custom: boolean;
}

/**
 * Collapses shifts that share a name across Bases into one group. Default shifts come first, then Base-specific
 * ones; within each, the order the names first appear.
 */
export function groupShiftsByName(
  shifts: Pick<AttendanceShiftOption, 'id' | 'name' | 'custom'>[]
): AttendanceShiftGroup[] {
  const groups = new Map<string, AttendanceShiftGroup>();
  for (const shift of shifts) {
    const key = shift.name.trim().toLowerCase();
    const group = groups.get(key);
    if (group) {
      group.ids.push(String(shift.id));
      group.custom = group.custom && shift.custom;
    } else {
      groups.set(key, {
        key,
        label: shift.name.trim(),
        ids: [String(shift.id)],
        custom: shift.custom,
      });
    }
  }
  return [...groups.values()].sort((a, b) => Number(a.custom) - Number(b.custom));
}
